import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ASTHMA_TRIGGERS = ['Pollen', 'Dust', 'Exercise', 'Cold Air', 'Smoke', 'Pet Dander', 'Stress'];
const JOINTS = ['Knee', 'Hip', 'Shoulder', 'Wrist', 'Ankle', 'Elbow', 'Fingers', 'Lower Back'];
const IBS_TRIGGERS = ['Dairy', 'Gluten', 'Spicy Food', 'Caffeine', 'Alcohol', 'Fried Food', 'High Fiber'];

export const DiabetesForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div><Label>Blood Sugar (mg/dL) *</Label><Input type="number" value={form.blood_sugar} onChange={(e) => setForm({ ...form, blood_sugar: e.target.value })} placeholder="e.g., 120" /></div>
    <div className="grid grid-cols-2 gap-4">
      <div><Label>Insulin Units</Label><Input type="number" value={form.insulin_units} onChange={(e) => setForm({ ...form, insulin_units: e.target.value })} placeholder="e.g., 5" /></div>
      <div><Label>Carbs (g)</Label><Input type="number" value={form.carbs} onChange={(e) => setForm({ ...form, carbs: e.target.value })} placeholder="e.g., 45" /></div>
    </div>
    <div><Label>Meal Type</Label>
      <select value={form.meal_type} onChange={(e) => setForm({ ...form, meal_type: e.target.value })} className="w-full px-3 py-2 border rounded-md">
        <option value="breakfast">Breakfast</option><option value="lunch">Lunch</option><option value="dinner">Dinner</option><option value="snack">Snack</option>
      </select>
    </div>
    <div><Label>Notes</Label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows="2" placeholder="Any additional notes..." /></div>
  </div>
);

export const HypertensionForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div><Label>Systolic *</Label><Input type="number" value={form.systolic} onChange={(e) => setForm({ ...form, systolic: e.target.value })} placeholder="e.g., 120" /></div>
      <div><Label>Diastolic *</Label><Input type="number" value={form.diastolic} onChange={(e) => setForm({ ...form, diastolic: e.target.value })} placeholder="e.g., 80" /></div>
    </div>
    <div><Label>Heart Rate (bpm)</Label><Input type="number" value={form.heart_rate} onChange={(e) => setForm({ ...form, heart_rate: e.target.value })} placeholder="e.g., 72" /></div>
    <div className="flex items-center gap-2"><input type="checkbox" checked={form.medication_taken} onChange={(e) => setForm({ ...form, medication_taken: e.target.checked })} className="w-4 h-4" /><Label>Medication taken today</Label></div>
    <div><Label>Notes</Label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows="2" /></div>
  </div>
);

export const AsthmaForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div><Label>Inhaler Puffs *</Label><Input type="number" value={form.inhaler_puffs} onChange={(e) => setForm({ ...form, inhaler_puffs: e.target.value })} placeholder="e.g., 2" /></div>
    <div><Label>Peak Flow (L/min)</Label><Input type="number" value={form.peak_flow} onChange={(e) => setForm({ ...form, peak_flow: e.target.value })} placeholder="e.g., 400" /></div>
    <div><Label>Triggers</Label>
      <div className="flex flex-wrap gap-2 mt-2">
        {ASTHMA_TRIGGERS.map(trigger => (
          <button key={trigger} onClick={() => { const triggers = form.triggers.includes(trigger) ? form.triggers.filter(t => t !== trigger) : [...form.triggers, trigger]; setForm({ ...form, triggers }); }} className={`px-3 py-1 rounded-full text-sm ${form.triggers.includes(trigger) ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-700'}`}>{trigger}</button>
        ))}
      </div>
    </div>
    <div><Label>Symptom Severity</Label>
      <select value={form.symptom_severity} onChange={(e) => setForm({ ...form, symptom_severity: e.target.value })} className="w-full px-3 py-2 border rounded-md">
        <option value="none">None</option><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option>
      </select>
    </div>
    <div><Label>Notes</Label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows="2" /></div>
  </div>
);

export const ArthritisForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div><Label>Pain Level (1-10): {form.pain_level}</Label><input type="range" min="1" max="10" value={form.pain_level} onChange={(e) => setForm({ ...form, pain_level: parseInt(e.target.value) })} className="w-full" /></div>
    <div><Label>Affected Joints</Label>
      <div className="flex flex-wrap gap-2 mt-2">
        {JOINTS.map(joint => (
          <button key={joint} onClick={() => { const affected_joints = form.affected_joints.includes(joint) ? form.affected_joints.filter(j => j !== joint) : [...form.affected_joints, joint]; setForm({ ...form, affected_joints }); }} className={`px-3 py-1 rounded-full text-sm ${form.affected_joints.includes(joint) ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'}`}>{joint}</button>
        ))}
      </div>
    </div>
    <div><Label>Stiffness Duration (minutes)</Label><Input type="number" value={form.stiffness_duration} onChange={(e) => setForm({ ...form, stiffness_duration: e.target.value })} placeholder="e.g., 30" /></div>
    <div><Label>Weather Conditions</Label><Input value={form.weather_conditions} onChange={(e) => setForm({ ...form, weather_conditions: e.target.value })} placeholder="e.g., Rainy, Cold, Humid" /></div>
    <div><Label>Notes</Label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows="2" /></div>
  </div>
);

export const IBSForm = ({ form, setForm }) => (
  <div className="space-y-4">
    <div><Label>Symptom Type</Label>
      <select value={form.symptom_type} onChange={(e) => setForm({ ...form, symptom_type: e.target.value })} className="w-full px-3 py-2 border rounded-md">
        <option value="diarrhea">Diarrhea</option><option value="constipation">Constipation</option><option value="cramping">Cramping</option><option value="bloating">Bloating</option>
      </select>
    </div>
    <div><Label>Severity (1-10): {form.severity}</Label><input type="range" min="1" max="10" value={form.severity} onChange={(e) => setForm({ ...form, severity: parseInt(e.target.value) })} className="w-full" /></div>
    <div><Label>Food Triggers</Label>
      <div className="flex flex-wrap gap-2 mt-2">
        {IBS_TRIGGERS.map(trigger => (
          <button key={trigger} onClick={() => { const food_triggers = form.food_triggers.includes(trigger) ? form.food_triggers.filter(t => t !== trigger) : [...form.food_triggers, trigger]; setForm({ ...form, food_triggers }); }} className={`px-3 py-1 rounded-full text-sm ${form.food_triggers.includes(trigger) ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'}`}>{trigger}</button>
        ))}
      </div>
    </div>
    <div><Label>Stress Level (1-10): {form.stress_level}</Label><input type="range" min="1" max="10" value={form.stress_level} onChange={(e) => setForm({ ...form, stress_level: parseInt(e.target.value) })} className="w-full" /></div>
    <div><Label>Notes</Label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows="2" /></div>
  </div>
);

const ConditionLogForm = ({ activeCondition, forms, setForms }) => {
  const { diabetesForm, hypertensionForm, asthmaForm, arthritisForm, ibsForm } = forms;
  const { setDiabetesForm, setHypertensionForm, setAsthmaForm, setArthritisForm, setIbsForm } = setForms;

  switch (activeCondition) {
    case 'diabetes': return <DiabetesForm form={diabetesForm} setForm={setDiabetesForm} />;
    case 'hypertension': return <HypertensionForm form={hypertensionForm} setForm={setHypertensionForm} />;
    case 'asthma': return <AsthmaForm form={asthmaForm} setForm={setAsthmaForm} />;
    case 'arthritis': return <ArthritisForm form={arthritisForm} setForm={setArthritisForm} />;
    case 'ibs': return <IBSForm form={ibsForm} setForm={setIbsForm} />;
    default: return null;
  }
};

export default ConditionLogForm;
